// FACT LEDGER (Phase R2) — the schema and the pure decision rules of CVCRAFT_RUBRIC_CODEBOOK_v1.md §2.
//
// This module is the foundation the codebook describes: "Every applicant-derived fact, and every count of
// applicant-derived facts, lives here and nowhere else." It is deliberately a PURE module — zero worker-runtime
// imports, zero I/O, zero LLM calls — so that every rule below is enumerable, deterministic, and testable
// (§11.3: band computation and fact validation run OUTSIDE the model).
//
// SCOPE BOUNDARY: R2 is EXTRACTION ONLY. There is no band computation anywhere in this file — no facet band, no
// domain band, no presentation band. Counting and status-precedence helpers ARE in scope (§2.4 "for counting
// only"); turning counts into bands is Phase R3.
//
// OFFSET MODEL (§2.7): all span offsets are UNICODE CODE-POINT offsets into NFC-NORMALIZED document text. They are
// NOT UTF-16 indices and NOT byte offsets. JS string indexing is UTF-16, so every slice in this file goes through a
// code-point array. The v1 "byte-exact" language is withdrawn by the codebook as incompatible with accented French.

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.7 — Evidence contract
 * ------------------------------------------------------------------------------------------------------------ */

/** Form 1 — positive-evidence span. Exactly one of `entry_id` / `layout_fact_id` anchors the span. */
export interface EvidenceSpan {
  entry_id?: string;
  layout_fact_id?: string;
  field: string;
  /** Exact substring of the NFC-normalized document text. */
  quoted_text: string;
  /** Unicode code-point offset into the NFC-normalized text, inclusive. */
  cp_start: number;
  /** Unicode code-point offset into the NFC-normalized text, exclusive. */
  cp_end: number;
}

/** Form 2 — closed-world omission evidence. Backs every claim that the CV does *not* state something. */
export interface SearchScope {
  claim_type: string;
  scope_description: string;
  entry_ids_assessed: string[];
  n_assessed: number;
  extractor_version: string;
}

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.1 — Provenance union
 * ------------------------------------------------------------------------------------------------------------ */

export type ProvenanceKind = 'extracted_span' | 'user_supplied' | 'derived' | 'closed_world';

/** Read from the submitted document. MUST carry a valid span (§2.7). Contract C1 applies ONLY to this variant. */
export interface ExtractedSpanProvenance {
  provenance: 'extracted_span';
  evidence_span: EvidenceSpan;
}

/** Entered or corrected by the user. Has no obligation to be a substring of the document. */
export interface UserSuppliedProvenance {
  provenance: 'user_supplied';
  /** The user's value. */
  user_value: string;
  /** The original span it replaced, where one existed. Nullable. */
  replaces_span: EvidenceSpan | null;
}

/** Computed by an enumerable rule from other ledger facts. Stores the rule id and the ids of its inputs. */
export interface DerivedProvenance {
  provenance: 'derived';
  rule_id: string;
  input_fact_ids: string[];
}

/**
 * §2.1 variant 4, ADDED IN CODEBOOK v1.6. Carried by a fact whose value is a not_stated/absence CONCLUSION that
 * was reached by assessing an enumerated scope rather than by reading a span.
 *
 * This is the variant that resolves the Rule 3 gap v1.5 left open. Rule 3 (§2.2) fires precisely when the entry
 * states nothing to derive from, so a `derived` provenance would have to carry zero input fact ids — which §2.1
 * forbids. The honest description of what Rule 3 actually did is a CLOSED-WORLD SEARCH: it looked at an enumerated
 * scope (this entry, these fields) and found no stated or tensed activity clause. That is a §2.7 Form 2 claim, and
 * it is now a first-class provenance rather than a `derived` fact with a hole in it.
 *
 * C1 does NOT apply (there is no span). C2b is satisfied through `search_scope`, which MUST enumerate a non-empty
 * scope — an omission claim with an empty scope MUST NOT render (§2.7 C2b).
 */
export interface ClosedWorldProvenance {
  provenance: 'closed_world';
  search_scope: SearchScope;
}

export type Provenance =
  | ExtractedSpanProvenance
  | UserSuppliedProvenance
  | DerivedProvenance
  | ClosedWorldProvenance;

/** A ledger fact: a value, a field, and a declared provenance. */
export type LedgerFact<T = unknown> = {
  fact_id: string;
  field: string;
  value: T;
  /**
   * §2.2a G6 / §4.4 (ii) — the user told us THIS FACT is wrong.
   *
   * Fact-level rather than entry-level on purpose. §2.2a's whole reason for making each semantic judgment its own
   * atomic fact is that "a user who tells us we misread 'led' cannot be made to retract the setting we read
   * correctly on the same line." A flagged fact is stripped before band computation (`excludeFlaggedFacts`); the
   * entry, and every other fact on it, survives.
   *
   * Optional and defaulting to absent, so it round-trips through `facts_json` without a schema change.
   */
  user_flagged_wrong?: true;
} & Provenance;

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.2a — ATOMIC SIGNAL FACTS (codebook v1.7)
 *
 * The closed registry of semantic judgments §3.1, §3.2 L2, §4.2 D3, §4.3, §5.7 and §7.2 Attribution are written
 * over. Through v1.6 these had NO SCHEMA, so R3 could not reach those rules from the ledger at all (GAP-1).
 *
 * THE SHAPE IS DELIBERATELY NOT A NEW RECORD TYPE. A signal is an ordinary `LedgerFact` living in `entry.facts[]`,
 * with `field` = `signal.<signal_type>` and a boolean value. That single decision is what makes every guarantee in
 * §2.2a fall out of machinery that already exists and is already tested: `checkProvenance` validates it, C1
 * validates its span, `expectedAnchor` enforces G4's same-entry rule, `checkC2aTraceable` enforces G3 on the
 * positive polarity, `checkC2bOmissionClaim` enforces G5 on the negative one, `facts_json` persists it, and the
 * GET round-trip returns it. A parallel record type, or a detached `Map` handed between phases, would have to
 * re-earn all of that — and §2.2a is explicit that a detached unvalidated map is non-conformant.
 * ------------------------------------------------------------------------------------------------------------ */

/**
 * The CLOSED 20-value registry (§2.2a Rule G2). An emission naming anything else is discarded and logged, never
 * mapped to the nearest member. Adding a type is an owner ratification of the codebook, not a code change.
 */
export const SIGNAL_TYPES = [
  // §3.1 Facet A — Responsibility
  'participatory_function',
  'bounded_object',
  'ownership_relation',
  'direction_of_whole',
  'coordination_of_others',
  'stated_independent_practice',
  // §3.2 Facet B — Completion
  'outcome_or_recognition_stated',
  // §4.2 D3 — the subtype family. D3 is satisfied when ANY member is positive.
  'd3_named_setting',
  'd3_stated_count',
  'd3_named_deliverable',
  'd3_named_audience',
  'd3_named_collaborating_group',
  // §7.2 / §3.2 L2 — applicability
  'describes_activity',
  // §5.7 — descriptor
  'collaboration_function',
  // §4.3 — domain assignment, one per assignable domain
  'domain_assignment_clinical',
  'domain_assignment_leadership',
  'domain_assignment_scholarship',
  'domain_assignment_service',
  'domain_assignment_teaching',
  // §2.2 / §4.2 — the opaque distinctness token
  'organization_context_token',
  // §3.3 L4 — the progression route, ADDED IN CODEBOOK v1.8 (amendment B).
  //
  // v1.7's §3.3 L4 row permitted "same line of work" to be established "from a stated progression clause", but no
  // registered signal carried that judgment — so the route was a promised path with no reachable implementation and
  // only the stated-organization route could ever fire L4. The signal's POSITIVE POLARITY IS NARROW BY RATIFICATION:
  // a quoted clause stating TWO roles, in ONE named activity/organization/project family, at TWO stated times. All
  // three conditions live in the signal's definition (and therefore in the extraction prompt), not in the engine —
  // the engine cannot re-check them without reading prose, which §11.3 forbids it to do.
  'stated_progression_clause',
] as const;

export type SignalType = (typeof SIGNAL_TYPES)[number];

const SIGNAL_TYPE_SET: ReadonlySet<string> = new Set<string>(SIGNAL_TYPES);

export function isSignalType(v: unknown): v is SignalType {
  return typeof v === 'string' && SIGNAL_TYPE_SET.has(v);
}

/** The §4.2 D3 subtype family (§2.2a). D3 is satisfied when ANY member is positive. */
export const D3_SIGNAL_TYPES = SIGNAL_TYPES.filter((t) => t.startsWith('d3_')) as readonly SignalType[];

/** The §4.3 domain-assignment family, in §4.1 display order. `entry.domains` is their projection. */
export const DOMAIN_ASSIGNMENT_SIGNAL_TYPES = {
  clinical: 'domain_assignment_clinical',
  leadership: 'domain_assignment_leadership',
  scholarship: 'domain_assignment_scholarship',
  service: 'domain_assignment_service',
  teaching: 'domain_assignment_teaching',
} as const satisfies Record<string, SignalType>;

/** The `field` a signal fact carries. One canonical spelling, used by minting and reading alike. */
export function signalField(type: SignalType): string {
  return `signal.${type}`;
}

/**
 * The `fact_id` a signal fact carries (§2.2a G1: the fact is owned by an entry).
 *
 * MINTED AND READ THROUGH THE SAME FUNCTION, deliberately. The R3 review found traces citing `W1:status` while
 * the ledger had minted `W1:status:0` — an id format invented at the read site that resolved to nothing. A single
 * constructor makes that class of defect unrepresentable for signals.
 */
export function signalFactId(entry_id: string, type: SignalType): string {
  return `${entry_id}:signal:${type}`;
}

/**
 * §2.2a Rule G5 — THREE STATES, NOT TWO.
 *
 * `positive`          — the document states it; the fact resolves through C2a.
 * `assessed_negative` — we searched an enumerated scope and it was not there; the fact carries a `search_scope`.
 * `unassessed`        — no fact of either polarity exists.
 *
 * The third state is the one the R3 review's blocker 1 was about. Defaulting a missing signal to "absent" asserts
 * a closed-world conclusion nobody performed — a C2b violation carried out by a default value. So `unassessed`
 * fires no rule in either direction, and caps the entry's confidence (§9.1).
 */
export type SignalState =
  | { state: 'positive'; fact: LedgerFact }
  | { state: 'assessed_negative'; fact: LedgerFact }
  | { state: 'unassessed' };

export type SignalReadFailure =
  | 'not_present'
  | 'excluded_by_user'
  | 'anchor_mismatch'
  | 'invalid_provenance'
  | 'positive_not_c2a_resolvable'
  | 'negative_without_scope'
  | 'non_boolean_value'
  /** G4 — a `derived` signal whose `input_fact_ids` do not all resolve to facts on THIS entry. */
  | 'derived_input_not_same_entry'
  /** G5 — an assessed negative whose scope assessed something other than exactly this entry. */
  | 'negative_scope_not_this_entry'
  /** G5 — an assessed negative whose scope does not identify WHICH signal was searched for. */
  | 'negative_scope_does_not_identify_signal'
  /** The persisted record is not a well-formed §2.2a fact at all (malformed `facts_json`, missing keys). */
  | 'malformed_record';

/**
 * Read one signal off an entry, ENFORCING §2.2a G1–G5 at the read site.
 *
 * Fails to `unassessed` rather than throwing, because a malformed signal is exactly the state that must not band:
 * a signal we cannot validate has told us nothing, and "told us nothing" is `unassessed`, never `absent`.
 *
 * @param validSpanFactIds fact ids whose spans PASSED C1 (`validateFacts(...).kept`). A positive signal backed by
 *        a span that failed C1 is NOT renderable under C2a and therefore must not band (G3).
 */
/**
 * The persisted `facts_json` boundary, made fail-closed (round-3 blocker): keep only members that are actually
 * fact-shaped. A non-array, a null member, or a member without string ids is dropped HERE, once, so every
 * consumer downstream iterates over facts it can safely touch. Dropping is correct because an unreadable record
 * has told us nothing — and "told us nothing" is `unassessed`, never a crash and never "absent".
 */
export function validFactsOf(entry: Pick<EntryRecord, 'facts'>): LedgerFact[] {
  if (!Array.isArray(entry.facts)) return [];
  return entry.facts.filter(
    (f): f is LedgerFact =>
      f !== null && typeof f === 'object' && typeof (f as { fact_id?: unknown }).fact_id === 'string' &&
      typeof (f as { field?: unknown }).field === 'string' && typeof (f as { provenance?: unknown }).provenance === 'string',
  );
}

export function readSignal(
  entry: EntryRecord,
  type: SignalType,
  validSpanFactIds: ReadonlySet<string>,
): SignalState & { failure?: SignalReadFailure } {
  const wantId = signalFactId(entry.entry_id, type);
  const wantField = signalField(type);
  // MALFORMED PERSISTED INPUT IS REJECTED HERE, not assumed away. `entry.facts` arrives from `facts_json`, which is
  // a JSON blob written by an earlier phase and re-read by this one; nothing in the type system survives that round
  // trip. A non-array, or an array carrying non-objects, is not a ledger we can read — and "cannot read" is
  // `unassessed`, never "absent" (G5). The round-2 review's point stands generally: production minting happens to
  // produce valid records, but a validation boundary that only holds for well-formed input is not a boundary.
  if (!Array.isArray(entry.facts)) return { state: 'unassessed', failure: 'malformed_record' };
  const valid = validFactsOf(entry);
  const dropped = entry.facts.length - valid.length;
  const fact = valid.find((f) => f.fact_id === wantId && f.field === wantField);
  if (!fact && dropped > 0) return { state: 'unassessed', failure: 'malformed_record' };
  if (!fact) return { state: 'unassessed', failure: 'not_present' };
  if (typeof fact.provenance !== 'string') return { state: 'unassessed', failure: 'malformed_record' };

  // G6 — an excluded fact is not merely down-weighted; it is not there.
  if (fact.user_flagged_wrong === true || entry.confirmation_state === 'user_flagged_wrong') {
    return { state: 'unassessed', failure: 'excluded_by_user' };
  }
  if (typeof fact.value !== 'boolean') return { state: 'unassessed', failure: 'non_boolean_value' };
  if (!checkProvenance(fact).ok) return { state: 'unassessed', failure: 'invalid_provenance' };

  // G4 — same-entry evidence. A span anchored elsewhere would send "show source" to another entry.
  if (fact.provenance === 'extracted_span' && fact.evidence_span.entry_id !== entry.entry_id) {
    return { state: 'unassessed', failure: 'anchor_mismatch' };
  }
  // G4, THE DERIVED HALF — enforced here for the first time (round-2 blocker 3). The reader checked recursive C2a
  // on a derived signal but never checked WHOSE facts it was derived from, so a derived signal could be computed
  // from another entry's facts and still band this one. G4's sentence is explicit that a derived signal's
  // `input_fact_ids` must resolve to facts on the same entry, and C2a resolution does not imply it: C2a asks
  // whether the inputs resolve, not whether they belong here.
  if (fact.provenance === 'derived') {
    if (!Array.isArray(fact.input_fact_ids) || fact.input_fact_ids.length === 0) {
      return { state: 'unassessed', failure: 'malformed_record' };
    }
    // Ownership is STRUCTURAL, not membership (round-3 probe: an `E2:` fact smuggled into E1's array passed the
    // membership test). A same-entry fact's id is prefixed with this entry's id by `signalFactId`/minting, so the
    // prefix is checked IN ADDITION to resolution within the validated array.
    const ownPrefix = `${entry.entry_id}:`;
    const ownIds = new Set(validFactsOf(entry).filter((f) => f.fact_id.startsWith(ownPrefix)).map((f) => f.fact_id));
    if (!fact.input_fact_ids.every((id) => typeof id === 'string' && id.startsWith(ownPrefix) && ownIds.has(id))) {
      return { state: 'unassessed', failure: 'derived_input_not_same_entry' };
    }
  }

  if (fact.value === true) {
    // G3 — a positive signal is a POSITIVE CLAIM and must resolve through C2a. `closed_world` cannot support one.
    const ownPrefix2 = `${entry.entry_id}:`;
    const byId = new Map<string, LedgerFact>(
      validFactsOf(entry).filter((f) => f.fact_id.startsWith(ownPrefix2)).map((f) => [f.fact_id, f]),
    );
    const trace = checkC2aTraceable([fact.fact_id], byId, validSpanFactIds);
    if (!trace.renderable) return { state: 'unassessed', failure: 'positive_not_c2a_resolvable' };
    return { state: 'positive', fact };
  }

  // G5 — an assessed negative is a closed-world conclusion over a NON-EMPTY scope.
  const omission = checkC2bOmissionClaim(fact);
  if (!omission.renderable) return { state: 'unassessed', failure: 'negative_without_scope' };

  // G5, THE TWO CONDITIONS THE NON-EMPTINESS CHECK DOES NOT COVER (round-2 blocker 3). §2.2a G5 does not merely
  // require *a* scope; it names exactly what the scope must say: `entry_ids_assessed[]` IS THAT ENTRY, `n_assessed`
  // is 1, and `scope_description` names the entry, the fields searched, AND THE SIGNAL SEARCHED FOR. A non-empty
  // scope over some other entry, or a scope that does not say which signal it looked for, is a closed-world claim
  // about a different question — and reading it as this signal's assessed absence asserts a conclusion nobody
  // reached, which is precisely the C2b-by-default-value failure G5 exists to prevent.
  const scope = (fact as { search_scope: SearchScope }).search_scope;
  const assessed = scope.entry_ids_assessed;
  if (!Array.isArray(assessed) || assessed.length !== 1 || assessed[0] !== entry.entry_id || scope.n_assessed !== 1) {
    return { state: 'unassessed', failure: 'negative_scope_not_this_entry' };
  }
  // The signal must be IDENTIFIED, in either of the two fields that can carry it. Substring matching on the
  // registered type name is deliberate and sufficient: the type names are the closed registry's own spellings, so
  // a scope that names one names it unambiguously, and a scope that names none has not told us what it searched for.
  const identifiesSignal =
    (typeof scope.claim_type === 'string' && scope.claim_type.includes(type)) ||
    (typeof scope.scope_description === 'string' && scope.scope_description.includes(type));
  if (!identifiesSignal) {
    return { state: 'unassessed', failure: 'negative_scope_does_not_identify_signal' };
  }
  return { state: 'assessed_negative', fact };
}

export interface EntrySignalReading {
  /** Every registered type, resolved to one of the three §2.2a G5 states. Total over `SIGNAL_TYPES`. */
  states: Record<SignalType, SignalState>;
  /** The types that came back `unassessed`. Non-empty ⇒ §9.1's signals-incomplete confidence cap fires. */
  unassessed: SignalType[];
  /** §9.1 — "signals incomplete". True iff at least one registered type was never assessed. */
  signals_incomplete: boolean;
}

/** Resolve ALL 20 registered signals for one entry. The total map is what makes `unassessed` observable. */
export function readEntrySignals(
  entry: EntryRecord,
  validSpanFactIds: ReadonlySet<string>,
): EntrySignalReading {
  const states = {} as Record<SignalType, SignalState>;
  const unassessed: SignalType[] = [];
  for (const type of SIGNAL_TYPES) {
    const s = readSignal(entry, type, validSpanFactIds);
    states[type] = s.state === 'positive' || s.state === 'assessed_negative' ? s : { state: 'unassessed' };
    if (s.state === 'unassessed') unassessed.push(type);
  }
  return { states, unassessed, signals_incomplete: unassessed.length > 0 };
}

/**
 * §4.4 (ii) / §2.2a G6 — strip user-excluded facts BEFORE anything bands.
 *
 * Returns a copy; the stored ledger is never mutated. An entry whose whole `confirmation_state` is
 * `user_flagged_wrong` loses every fact — the user told us we read the entry wrong, not one field of it.
 */
export function excludeFlaggedFacts(entries: readonly EntryRecord[]): EntryRecord[] {
  return entries.map((e) => {
    if (e.confirmation_state === 'user_flagged_wrong') return { ...e, facts: [] };
    // Fail-closed over malformed persisted `facts` (round-3 blocker): normalize through `validFactsOf` FIRST so a
    // null member or a non-array blob cannot throw out of the exclusion pass — it is dropped, and dropped means
    // unassessed downstream, never a crash.
    const valid = validFactsOf(e);
    const kept = valid.filter((f) => f.user_flagged_wrong !== true);
    return kept.length === (Array.isArray(e.facts) ? e.facts.length : -1) ? e : { ...e, facts: kept };
  });
}

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.2 — Entry record
 * ------------------------------------------------------------------------------------------------------------ */

export const NOT_STATED = 'not_stated' as const;
export type NotStated = typeof NOT_STATED;

/** `YYYY`, `YYYY-MM`, or `not_stated`. Partial dates are FIRST-CLASS, never errors (§2.2). */
export type DatePartial = string | NotStated;

export type SourceSection =
  | 'research' | 'publications' | 'education' | 'work' | 'volunteer'
  | 'leadership' | 'teaching' | 'awards' | 'presentations' | 'other' | 'unlabelled';

export type EntryType =
  | 'clinical' | 'research_project' | 'scholarly_work' | 'teaching' | 'leadership_role'
  | 'service' | 'employment' | 'award' | 'certification' | 'membership'
  // 2026-08-23 — the two commonest medical-CV entries the enum had no member for. Without them a degree and a
  // language proficiency both arrived as `other`, fell to the unplaceable profile, and were critiqued as if they
  // were projects (owner: "it was not smart in knowing that medical cv does not need metrics or specific details
  // in certain roles or duties"). Each now has its own §2.2b profile.
  | 'education' | 'language'
  | 'other';

/**
 * THE ENTRY TYPES THE DATABASE CAN ACTUALLY STORE TODAY — the gate, and the ONLY list any write path may consult.
 *
 * `EntryType` is the union the ENGINE reasons over; this is the subset `fact_ledger_entries.entry_type`'s CHECK
 * constraint admits. They are not the same set while a new member is built but its migration has not been applied
 * remotely, and on 2026-08-23 that gap shipped: `education` and `language` were added to the union, to the
 * extraction prompt, to the profile table AND to the correction validator, while the CHECK still listed the
 * original eleven. Any write of either value — a fresh extraction, or an applicant correcting an entry's type in
 * the facts editor — would have been accepted by the application and then REFUSED by D1.
 *
 * SO EVERY WRITER READS THIS, not the union: the extractor's accepted set, the correction validator, and the
 * editor's option list. `T-ENUM-DB-1` asserts this set is a subset of the CHECK parsed out of the migration DDL,
 * and that every writer's set is a subset of THIS — so a value can only become writable by widening the schema.
 *
 * TO OPEN THE GATE: apply the rebuild migration that widens the CHECK (see CVCRAFT_R5_CUTOVER_RUNBOOK.md §I),
 * add the members here, and bump `EXTRACTOR_VERSION` in the same change.
 */
export const STORABLE_ENTRY_TYPES: readonly EntryType[] = [
  'clinical', 'research_project', 'scholarly_work', 'teaching', 'leadership_role', 'service', 'employment',
  'award', 'certification', 'membership', 'education', 'language', 'other',
  // GATE OPENED 2026-08-23: migration 0026 widened the `fact_ledger_entries.entry_type` CHECK on the remote D1
  // (verified post-apply — 13 members, all row counts unchanged, `foreign_key_check` clean). `T-ENUM-DB-1` reads
  // the CHECK out of the migration DDL, so this list and the schema are now asserted equal rather than assumed.
];

export type ActivityStatus = 'completed' | 'under_way' | NotStated;

export type Frequency = 'one_off' | 'weekly' | 'monthly' | 'per_term' | 'irregular' | 'continuous';

export type SupervisionLevel =
  | 'observer_shadowing' | 'supervised_student' | 'supervised_trainee_postgrad'
  | 'stated_independent' | 'non_clinical';

export type TrainingStage = 'pre_med' | 'undergrad_med' | 'postgrad' | 'post_licensure' | 'non_medical';

/** §2.8 */
export type ConfirmationState =
  | 'extracted' | 'user_confirmed' | 'user_edited' | 'user_flagged_wrong' | 'user_declined_to_confirm';

export interface EntryRecord {
  entry_id: string;
  /** Verbatim. Never paraphrased into storage. */
  raw_text: string;
  /** As labelled by the document. A hint, never the domain assignment (§4.3). */
  source_section: SourceSection;
  entry_type: EntryType;
  date_start: DatePartial;
  date_end: DatePartial;
  is_ongoing: boolean | NotStated;
  activity_status: ActivityStatus;
  /** Verbatim as written ("6 weeks"). Derived duration is a separate `derived` fact. */
  duration_stated: string | NotStated;
  frequency: Frequency | NotStated;
  role_title: string | NotStated;
  /** The verbatim clause describing what the applicant did. Distinct from title. */
  responsibility_statement: string | NotStated;
  supervision_level: SupervisionLevel | NotStated;
  /** Stated context only. MUST NOT be used as prestige or as a domain input. */
  jurisdiction: string | NotStated;
  training_stage_at_time: TrainingStage | NotStated;
  team_size: number | NotStated;
  /** Stripped from the content assessment representation (§11.2). */
  institution: string | NotStated;
  /** Opaque distinctness token. Equality/inequality comparison ONLY (§4.2, §11.2). */
  organization_context_id: string | NotStated;
  /** Assigned per §4.3. An entry may carry multiple. */
  domains: string[];
  /** Per §5. */
  descriptors: Record<string, unknown>;
  confirmation_state: ConfirmationState;
  facts: LedgerFact[];
}

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.3 — Document-layout fact record
 * ------------------------------------------------------------------------------------------------------------ */

export type LayoutObservationType =
  | 'section_heading_present' | 'section_heading_absent' | 'entries_grouped_by_section'
  | 'date_present_on_entry' | 'date_absent_on_entry' | 'date_format_variant'
  | 'section_date_order_consistent' | 'section_date_order_inconsistent'
  | 'entry_structure_signature' | 'output_status_labelled' | 'output_status_unlabelled'
  | 'role_distinguishable_from_project' | 'role_not_distinguishable';

/**
 * §2.3. `work` added in codebook v1.7 by the status-labelling scope ruling: `output_status_labelled` /
 * `output_status_unlabelled` are keyed by `work_id`, exactly one per DEDUPLICATED work, because §7.2's Status
 * labelling denominator is "all scholarly `work`s". Emitting one per entry puts a chain-listed work into the
 * denominator once per entry and lets a listing convention move a presentation band.
 */
export type LayoutScope = 'document' | 'section' | 'entry' | 'work';

export type OrderDirection = 'reverse_chronological' | 'chronological' | 'mixed' | 'not_applicable';

/**
 * The third evidence form a §2.3 layout fact can carry: a POSITIVE observation that no span states and no absence
 * describes, because it was COMPUTED mechanically from other ledger facts.
 *
 * §2.3 says layout facts carry provenance `extracted_span` or `derived`, and §2.1 (v1.6) reserves `closed_world`
 * for absence conclusions. A structure signature, a section's date ordering, and "these entries carry a
 * document-supplied section label" are all POSITIVE claims — C2a correctly refuses to let a `closed_world` fact
 * support a positive claim — so they are `derived`, and like every other `derived` fact they name the rule that
 * computed them and the inputs it consumed (§2.1 rule 1).
 *
 * `input_fact_ids` here may name an ENTRY ID rather than a per-field fact id where the mechanical input genuinely
 * is the entry record itself (its document-supplied `source_section` label, which is a column on the entry row and
 * not a spanned per-field fact). That is stated rather than papered over: inventing a `${id}:source_section` fact
 * id that no ledger row carries would make the derivation chain unwalkable in exactly the way C2a exists to catch.
 */
export interface DerivedLayoutEvidence {
  rule_id: string;
  input_fact_ids: string[];
}

/** True iff this evidence record is the §2.7 Form 1 span form. */
export function isSpanEvidence(e: EvidenceSpan | SearchScope | DerivedLayoutEvidence): e is EvidenceSpan {
  return 'quoted_text' in e;
}

/** True iff this evidence record is the computed/`derived` form (see `DerivedLayoutEvidence`). */
export function isDerivedEvidence(
  e: EvidenceSpan | SearchScope | DerivedLayoutEvidence,
): e is DerivedLayoutEvidence {
  return 'rule_id' in e && 'input_fact_ids' in e;
}

export interface DocumentLayoutFact {
  layout_fact_id: string;
  observation_type: LayoutObservationType;
  scope: LayoutScope;
  /**
   * The §2.4 work this observation is about, for `scope: 'work'` observations only (codebook v1.7).
   *
   * Present exactly when `scope === 'work'`, which today means the `output_status_labelled` /
   * `output_status_unlabelled` pair. It exists so §7.2's Status labelling denominator can be counted over
   * DEDUPLICATED works: without it the presentation layer would have to infer which work an observation belonged
   * to, and inferring identity is how a chain-listed work gets counted three times.
   */
  work_id?: string;
  /**
   * One of the three forms, chosen by WHAT THE OBSERVATION CLAIMS, never by its scope:
   *   a stated presence  → a §2.7 Form 1 span            (`extracted_span`)
   *   an absence         → a §2.7 Form 2 search_scope    (`closed_world`, §2.1 v1.6)
   *   a computed positive→ a rule id + its inputs        (`derived`)
   */
  evidence: EvidenceSpan | SearchScope | DerivedLayoutEvidence;
  count_assessed: number;
  count_matching: number;
  order_direction: OrderDirection;
  /** Opaque canonical shape token. Carries no content and is never rendered. */
  structure_signature: string | 'not_applicable';
}

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.4 — Scholarly work record
 * ------------------------------------------------------------------------------------------------------------ */

export type WorkType =
  | 'empirical_study' | 'review_synthesis' | 'case_report' | 'qi_project' | 'thesis'
  | 'educational_material' | 'commentary_editorial' | 'other_scholarly';

export type AuthorshipPosition =
  | 'first' | 'co_first_stated' | 'middle' | 'last_senior' | 'corresponding_stated'
  | 'group_consortium' | 'position_not_determinable';

export type AuthorOrderConvention =
  | 'contribution_ordered_assumed' | 'alphabetical_apparent' | 'consortium' | 'unknown';

export type ResultDirection = 'positive' | 'null_or_negative' | 'mixed' | NotStated;

export type WorkStatus =
  | 'in_preparation' | 'submitted_under_review' | 'accepted_not_yet_presented'
  | 'accepted_in_press' | 'published' | 'withdrawn_retired' | 'status_not_stated';

export interface StatusHistoryEntry {
  status: WorkStatus;
  year: number | NotStated;
  /** Full provenance union, exactly as §2.1. A span is required only for `extracted_span` (T-EVID-1). */
  evidence: Provenance;
}

export type DisseminationEventType =
  | 'poster' | 'oral_presentation' | 'abstract_publication' | 'journal_article'
  | 'preprint' | 'thesis_deposit' | 'report_to_committee' | 'public_facing_material';

/** Descriptor, not rank (§5.1). */
export type VenueScope = 'institutional' | 'local' | 'provincial_regional' | 'national' | 'international';

export type AcceptanceStated = 'accepted_stated' | 'invited_stated' | NotStated;

/** One of these MUST hold for a dissemination_event record to exist (§2.4). */
export type Occurrence = 'dated' | 'stated_past';

export interface DisseminationEvent {
  event_type: DisseminationEventType;
  venue_name: string | NotStated;
  venue_scope: VenueScope | NotStated;
  acceptance_stated: AcceptanceStated;
  year: number | NotStated;
  occurrence: Occurrence;
  evidence: Provenance;
}

/** Separate, user-confirmed only. The system MUST NOT make an autonomous indexing determination (§2.4). */
export interface WorkIndexing {
  indexed: 'indexed_confirmed_by_user' | NotStated;
}

export interface WorkRecord {
  /** ALSO the deduplication key — the v1 `dedup_group_id` is folded into it (§2.4). */
  work_id: string;
  /** REQUIRED linkage. Every entry_id representing this work. May contain one id. */
  entry_ids: string[];
  work_type: WorkType;
  title_stated: string | NotStated;
  authorship_position: AuthorshipPosition | NotStated;
  author_count: number | NotStated;
  author_order_convention: AuthorOrderConvention;
  contribution_statement: string | NotStated;
  /** Recorded for completeness only; MUST NOT affect any band (§3.6 edge case B). */
  result_direction: ResultDirection;
  status_history: StatusHistoryEntry[];
  dissemination_events: DisseminationEvent[];
  indexing: WorkIndexing;
}

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.5 — Grants, awards, recognition record
 * ------------------------------------------------------------------------------------------------------------ */

export type RecognitionType =
  | 'grant' | 'scholarship' | 'prize_award' | 'honour_society' | 'competitive_selection' | 'not_determinable';

/** Descriptor, not rank. */
export type AwardScope = 'course_level' | 'institutional' | 'provincial_regional' | 'national' | 'international';

export type SelectionBasis = 'merit_competitive' | 'need_based' | 'nomination' | 'participation' | NotStated;

export interface RecognitionRecord {
  recognition_type: RecognitionType;
  award_scope: AwardScope | NotStated;
  selection_basis_stated: SelectionBasis;
  /** Displayed verbatim if the user wrote it; never solicited, never used in any band. */
  monetary_value: string | NotStated;
  year: number | NotStated;
  /**
   * ADDED round 3. §2.5's own C3 span firewall (`spannedValue` in factExtract.ts) validates each stated subfield
   * against a real span before admitting it — but a validated span that is never retained is evidence discarded
   * the moment it is proven. One entry per subfield that survived the firewall; a field absent here either was
   * `not_stated` or could not be quoted. Storage-only — not part of the §2.5 shape the model or the rest of this
   * module reasons about, which is why it is optional.
   */
  field_evidence?: Record<string, EvidenceSpan>;
}

/* ------------------------------------------------------------------------------------------------------------ *
 * §2.6 — Clinical entry record
 * ------------------------------------------------------------------------------------------------------------ */

export type ExposureType =
  | 'observership' | 'elective' | 'core_rotation' | 'selective'
  | 'employment_clinical' | 'volunteer_clinical' | 'simulation' | NotStated;

export type PatientContactStated = 'stated_direct' | 'stated_observational' | NotStated;

export type LicenceStatus =
  | 'unlicensed' | 'student_registration' | 'provisional_licence' | 'full_licence' | NotStated;

export interface ClinicalEntryRecord {
  setting: string | NotStated;
  discipline: string | NotStated;
  exposure_type: ExposureType;
  weeks_stated: number | NotStated;
  patient_contact_stated: PatientContactStated;
  licence_status_at_time: LicenceStatus | NotStated;
  /** ADDED round 3. Same rationale as `RecognitionRecord.field_evidence` — see there. */
  field_evidence?: Record<string, EvidenceSpan>;
}

/* ============================================================================================================== *
 * PURE VALIDATION — C1 span validation (§2.7)
 * ============================================================================================================== */

/** NFC-normalize and explode to a code-point array. `Array.from` iterates code points, not UTF-16 units. */
export function nfcCodePoints(document: string): string[] {
  return Array.from(document.normalize('NFC'));
}

/**
 * Slice the NFC-normalized document by CODE-POINT offsets. Using `String.prototype.slice` here would be a bug:
 * it indexes UTF-16 units, so an astral-plane character ahead of the span shifts every later offset by one.
 */
export function sliceByCodePoints(cps: string[], cpStart: number, cpEnd: number): string {
  return cps.slice(cpStart, cpEnd).join('');
}

export type C1Failure =
  | 'offsets_not_integers'
  | 'offsets_out_of_order'
  | 'offsets_out_of_range'
  | 'quoted_text_mismatch'
  /** The STORED quote was not itself in NFC. §2.7 Form 1 requires the canonical stored representation to be NFC. */
  | 'quoted_text_not_nfc'
  /** §2.7 Form 1 declares `entry_id | layout_fact_id` — exactly one, never both, never neither. */
  | 'anchor_cardinality'
  /** The span's anchor id is not the id of the record that carries the span. */
  | 'anchor_mismatch';

/** Why a quote could not be turned into a span at all (extraction-side, before C1 ever runs). */
export type SpanSourcingFailure = 'ambiguous' | 'sourced_from_imperative_region';

export interface ExtractionError {
  fact_id: string;
  /** 'C1' for span failures, 'C4' for a source that is not eligible to found a fact, 'PROVENANCE' otherwise. */
  contract: 'C1' | 'C4' | 'PROVENANCE';
  reason: C1Failure | ProvenanceFailure | SpanSourcingFailure;
  detail: string;
}

export interface C1Result {
  ok: boolean;
  reason?: C1Failure;
  detail?: string;
}

/**
 * C1: `nfc(document)[cp_start:cp_end] === quoted_text`, on CODE-POINT offsets into NFC-normalized text.
 *
 * Takes an already-exploded code-point array so callers validating many facts pay the normalization cost once.
 * Never throws — a violating fact is reported, and the caller discards + logs it.
 *
 * This function is only ever reachable for `extracted_span` provenance: its parameter type is `EvidenceSpan`, and
 * the only caller that dispatches on provenance (`validateFacts`) enters this branch under a `extracted_span`
 * discriminant check (T-C1b / T-EVID-1: zero C1 evaluations attempted on non-span provenance).
 */
export function checkC1Span(span: EvidenceSpan, documentCodePoints: string[]): C1Result {
  const { cp_start, cp_end, quoted_text } = span;

  // ANCHOR CARDINALITY (§2.7 Form 1: `entry_id | layout_fact_id`). v1.5 code accepted both, or either — the
  // declared cardinality is EXACTLY ONE, and a span anchored to both belongs to no single record, so "show source"
  // has no defined destination. Checked here rather than only in `isSpanShaped` so a span that is merely
  // well-TYPED still cannot pass C1 while pointing two ways at once.
  const hasEntry = typeof span.entry_id === 'string' && span.entry_id.length > 0;
  const hasLayout = typeof span.layout_fact_id === 'string' && span.layout_fact_id.length > 0;
  if (hasEntry === hasLayout) {
    return {
      ok: false,
      reason: 'anchor_cardinality',
      detail: hasEntry ? 'span carries BOTH entry_id and layout_fact_id' : 'span carries NEITHER entry_id nor layout_fact_id',
    };
  }

  if (!Number.isInteger(cp_start) || !Number.isInteger(cp_end)) {
    return { ok: false, reason: 'offsets_not_integers', detail: `cp_start=${cp_start} cp_end=${cp_end}` };
  }
  if (cp_end < cp_start) {
    return { ok: false, reason: 'offsets_out_of_order', detail: `cp_start=${cp_start} cp_end=${cp_end}` };
  }
  if (cp_start < 0 || cp_end > documentCodePoints.length) {
    return {
      ok: false,
      reason: 'offsets_out_of_range',
      detail: `cp_start=${cp_start} cp_end=${cp_end} doc_cp_len=${documentCodePoints.length}`,
    };
  }
  // SPAN EXACTNESS (§2.7 Form 1): `quoted_text` is "exact substring of the NFC-normalized document text". The
  // STORED quote must therefore ALREADY be NFC — it is not enough that it normalizes to the right thing.
  //
  // v1.5 compared `actual !== quoted_text.normalize('NFC')`, which quietly ACCEPTED a decomposed quote: the stored
  // fact then failed its own contract, because `nfc(document)[cp_start:cp_end] == quoted_text` is literally false
  // for that record. Every consumer that re-checks C1 (the API's belt-and-braces pass, R5's "show source", any
  // future export) would disagree with the extractor about whether the fact is valid. We reject instead of
  // normalizing, because normalizing here would silently rewrite an applicant's verbatim text (§2.7, T-BAN-1
  // `applicant_verbatim`) to make a broken record pass.
  if (quoted_text !== quoted_text.normalize('NFC')) {
    return {
      ok: false,
      reason: 'quoted_text_not_nfc',
      detail: `stored quote is not NFC: ${JSON.stringify(quoted_text)}`,
    };
  }
  const actual = sliceByCodePoints(documentCodePoints, cp_start, cp_end);
  if (actual !== quoted_text) {
    return { ok: false, reason: 'quoted_text_mismatch', detail: `expected=${JSON.stringify(quoted_text)} actual=${JSON.stringify(actual)}` };
  }
  return { ok: true };
}

/* ============================================================================================================== *
 * PURE VALIDATION — provenance rules (§2.1)
 * ============================================================================================================== */

export type ProvenanceFailure =
  | 'unknown_provenance'
  | 'extracted_span_missing_span'
  | 'extracted_span_invalid_span_shape'
  | 'user_supplied_missing_value'
  | 'user_supplied_missing_replaces_span_field'
  | 'derived_missing_rule_id'
  | 'derived_missing_input_fact_ids'
  | 'closed_world_missing_search_scope'
  | 'closed_world_empty_scope'
  | 'closed_world_scope_count_mismatch';

export interface ProvenanceResult {
  ok: boolean;
  reason?: ProvenanceFailure;
  detail?: string;
}

function isSpanShaped(s: unknown): s is EvidenceSpan {
  if (typeof s !== 'object' || s === null) return false;
  const v = s as Record<string, unknown>;
  // EXACTLY ONE anchor (§2.7 Form 1: `entry_id | layout_fact_id`). Both-anchors is a shape error here, not merely
  // a C1 failure, so a span carrying two anchors cannot reach a ledger table at all.
  const hasEntry = typeof v.entry_id === 'string' && v.entry_id.length > 0;
  const hasLayout = typeof v.layout_fact_id === 'string' && v.layout_fact_id.length > 0;
  return (
    typeof v.field === 'string' &&
    typeof v.quoted_text === 'string' &&
    typeof v.cp_start === 'number' &&
    typeof v.cp_end === 'number' &&
    hasEntry !== hasLayout
  );
}

/** Is a value a §2.7 Form 2 `search_scope`, with a NON-EMPTY enumerated closed world (C2b)? */
export function checkSearchScope(scope: unknown): ProvenanceResult {
  if (typeof scope !== 'object' || scope === null) {
    return { ok: false, reason: 'closed_world_missing_search_scope' };
  }
  const s = scope as Record<string, unknown>;
  if (
    typeof s.claim_type !== 'string' || s.claim_type.length === 0 ||
    typeof s.scope_description !== 'string' || s.scope_description.length === 0 ||
    typeof s.extractor_version !== 'string' || s.extractor_version.length === 0 ||
    !Array.isArray(s.entry_ids_assessed) ||
    typeof s.n_assessed !== 'number'
  ) {
    return { ok: false, reason: 'closed_world_missing_search_scope' };
  }
  // "An omission claim with no scope MUST NOT render" (§2.7 C2b). An empty enumeration IS no scope.
  if (s.entry_ids_assessed.length === 0) return { ok: false, reason: 'closed_world_empty_scope' };
  // The rendered surface says "assessed across all N entries in your CV" off `n_assessed`, and opens the
  // enumeration off `entry_ids_assessed`. If those two disagree, the surface states a number its own evidence
  // does not support, so the disagreement is a validation failure rather than a display preference.
  if (s.n_assessed !== s.entry_ids_assessed.length) {
    return {
      ok: false,
      reason: 'closed_world_scope_count_mismatch',
      detail: `n_assessed=${s.n_assessed} but entry_ids_assessed has ${s.entry_ids_assessed.length}`,
    };
  }
  return { ok: true };
}

/**
 * Validate that a provenance record carries the fields its variant requires (§2.1):
 *   extracted_span → a span; user_supplied → the user's value AND an explicit (nullable) `replaces_span`;
 *   derived → a rule id and the ids of its inputs.
 * Does NOT evaluate C1 — that is a separate contract with a separate call site.
 */
export function checkProvenance(p: Provenance): ProvenanceResult {
  switch (p.provenance) {
    case 'extracted_span': {
      if (p.evidence_span === undefined || p.evidence_span === null) {
        return { ok: false, reason: 'extracted_span_missing_span' };
      }
      if (!isSpanShaped(p.evidence_span)) {
        return { ok: false, reason: 'extracted_span_invalid_span_shape' };
      }
      return { ok: true };
    }
    case 'user_supplied': {
      if (typeof p.user_value !== 'string') return { ok: false, reason: 'user_supplied_missing_value' };
      // `replaces_span` is nullable but NOT optional: null is "there was no span it replaced".
      if (!('replaces_span' in p) || p.replaces_span === undefined) {
        return { ok: false, reason: 'user_supplied_missing_replaces_span_field' };
      }
      if (p.replaces_span !== null && !isSpanShaped(p.replaces_span)) {
        return { ok: false, reason: 'extracted_span_invalid_span_shape' };
      }
      return { ok: true };
    }
    case 'derived': {
      if (typeof p.rule_id !== 'string' || p.rule_id.length === 0) {
        return { ok: false, reason: 'derived_missing_rule_id' };
      }
      if (!Array.isArray(p.input_fact_ids) || p.input_fact_ids.length === 0) {
        return { ok: false, reason: 'derived_missing_input_fact_ids' };
      }
      return { ok: true };
    }
    case 'closed_world':
      // §2.1 variant 4 (v1.6). No span, no input facts — the evidence IS the enumerated scope.
      return checkSearchScope(p.search_scope);
    default:
      return { ok: false, reason: 'unknown_provenance', detail: String((p as { provenance?: unknown }).provenance) };
  }
}

/* ============================================================================================================== *
 * Fact-set validation — the single dispatch point
 * ============================================================================================================== */

export interface ValidateFactsOptions {
  /**
   * Test/telemetry hook fired IMMEDIATELY BEFORE each C1 evaluation. It exists so T-C1b's "zero C1 evaluations
   * attempted on non-span provenance" can be asserted mechanically rather than by inspection.
   */
  onC1Attempt?: (fact: LedgerFact) => void;
  /**
   * The anchor the CONTAINING record has. A span whose `entry_id`/`layout_fact_id` names some OTHER record is
   * rejected: without this, a fact on entry E2 could carry a span anchored to E1, satisfy C1 (the quote really is
   * at those canonical offsets), and still send "show source" to the wrong entry. C1 checks the offsets against
   * the document; this checks that the fact and its evidence are talking about the same record.
   */
  expectedAnchor?: (fact: LedgerFact) => { entry_id?: string; layout_fact_id?: string } | null;
}

export interface ValidateFactsResult {
  /** Facts that satisfied their provenance rules and (where applicable) C1. */
  kept: LedgerFact[];
  /** Facts discarded, each with the reason logged as an extraction error (§2.7 C1). */
  errors: ExtractionError[];
}

/**
 * Validate a fact set against §2.1 provenance rules and §2.7 C1. Violating facts are DISCARDED AND LOGGED —
 * this function never throws.
 *
 * C1 is evaluated inside the `extracted_span` branch ONLY. `user_supplied` and `derived` facts never reach
 * `checkC1Span`, and the `onC1Attempt` hook makes that provable.
 */
export function validateFacts(
  facts: readonly LedgerFact[],
  document: string,
  options: ValidateFactsOptions = {},
): ValidateFactsResult {
  const cps = nfcCodePoints(document);
  const kept: LedgerFact[] = [];
  const errors: ExtractionError[] = [];

  for (const fact of facts) {
    const prov = checkProvenance(fact);
    if (!prov.ok) {
      errors.push({
        fact_id: fact.fact_id,
        contract: 'PROVENANCE',
        reason: prov.reason ?? 'unknown_provenance',
        detail: prov.detail ?? '',
      });
      continue;
    }

    if (fact.provenance === 'extracted_span') {
      const want = options.expectedAnchor?.(fact);
      if (want) {
        const span = fact.evidence_span;
        const anchorOk = want.entry_id !== undefined
          ? span.entry_id === want.entry_id
          : span.layout_fact_id === want.layout_fact_id;
        if (!anchorOk) {
          errors.push({
            fact_id: fact.fact_id,
            contract: 'C1',
            reason: 'anchor_mismatch',
            detail: `span anchored to ${JSON.stringify(span.entry_id ?? span.layout_fact_id)}, fact belongs to ${JSON.stringify(want.entry_id ?? want.layout_fact_id)}`,
          });
          continue;
        }
      }
      options.onC1Attempt?.(fact);
      const c1 = checkC1Span(fact.evidence_span, cps);
      if (!c1.ok) {
        errors.push({
          fact_id: fact.fact_id,
          contract: 'C1',
          reason: c1.reason ?? 'quoted_text_mismatch',
          detail: c1.detail ?? '',
        });
        continue;
      }
    }
    // `user_supplied`, `derived` and `closed_world` fall through with NO C1 evaluation (§2.7 C1, T-C1b, T-EVID-1).

    kept.push(fact);
  }

  return { kept, errors };
}

/** Validate the provenance union carried by `status_history[].evidence` / `dissemination_events[].evidence`. */
export function toLedgerFact<T>(fact_id: string, field: string, value: T, evidence: Provenance): LedgerFact<T> {
  switch (evidence.provenance) {
    case 'extracted_span':
      return { fact_id, field, value, provenance: 'extracted_span', evidence_span: evidence.evidence_span };
    case 'user_supplied':
      return {
        fact_id, field, value,
        provenance: 'user_supplied',
        user_value: evidence.user_value,
        replaces_span: evidence.replaces_span,
      };
    case 'closed_world':
      return { fact_id, field, value, provenance: 'closed_world', search_scope: evidence.search_scope };
    default:
      return {
        fact_id, field, value,
        provenance: 'derived',
        rule_id: evidence.rule_id,
        input_fact_ids: evidence.input_fact_ids,
      };
  }
}

export function validateWorkEvidence(
  work: WorkRecord,
  document: string,
  options: ValidateFactsOptions = {},
): ValidateFactsResult {
  const facts: LedgerFact[] = [
    ...work.status_history.map((s, i) =>
      toLedgerFact(`${work.work_id}:status:${i}`, 'status_history.status', s.status, s.evidence)),
    ...work.dissemination_events.map((e, i) =>
      toLedgerFact(`${work.work_id}:event:${i}`, 'dissemination_events.event_type', e.event_type, e.evidence)),
  ];
  return validateFacts(facts, document, options);
}

/* ============================================================================================================== *
 * C2a / C2b — TRACEABILITY, with RENDER-BLOCKING semantics (§2.7)
 *
 * C1 asks "is this span true against the document". C2 asks the different and, for the surface, more important
 * question: "may this claim be shown to the user at all". The codebook's language is render-blocking, not
 * advisory — "An omission claim with no scope MUST NOT render" — so these functions return a RENDERABLE verdict
 * that a caller is expected to obey, and they fail CLOSED: anything they cannot verify is not renderable.
 * ============================================================================================================== */

export type TraceabilityFailure =
  | 'no_supporting_facts'          // C2a: the claim cites nothing at all
  | 'unknown_input_fact'           // C2a: a `derived` fact names an input id that is not in the ledger
  | 'input_cycle'                  // C2a: `derived` inputs form a cycle, so nothing bottoms out in evidence
  | 'span_fact_failed_c1'          // C2a: the only support is an extracted_span fact whose span does not validate
  | 'omission_claim_without_scope' // C2b: an omission claim whose fact carries no search_scope
  | 'scope_invalid'                // C2b: the search_scope itself does not satisfy §2.7 Form 2
  /** C2b: the fact is the USER's own absence statement. It renders as a user statement (C2a), never as ours. */
  | 'user_statement_not_omission_claim';

export interface TraceabilityResult {
  /** MUST be obeyed by the surface. False ⇒ the claim does not render (§2.7 C2a/C2b). */
  renderable: boolean;
  reason?: TraceabilityFailure;
  detail?: string;
}

/**
 * C2a. "Every rendered positive claim MUST be traceable to ≥1 fact with a valid span, or to a `user_supplied`
 * fact, or to a `derived` fact whose inputs recursively satisfy C2a."
 *
 * RECURSIVE, as written. A `derived` fact is renderable only if it bottoms out, through however many derivation
 * hops, in a validated span or a user-supplied value. An input id we cannot resolve, or a cycle, blocks the render
 * — a claim whose evidence chain we cannot walk is exactly the claim the contract exists to stop.
 *
 * @param validSpanFactIds the fact ids whose spans PASSED C1. Callers pass `validateFacts(...).kept`, so a fact
 *        that failed C1 is not merely absent from the ledger — it cannot support a claim either.
 */
export function checkC2aTraceable(
  claimFactIds: readonly string[],
  factsById: ReadonlyMap<string, LedgerFact>,
  validSpanFactIds: ReadonlySet<string>,
): TraceabilityResult {
  if (claimFactIds.length === 0) {
    return { renderable: false, reason: 'no_supporting_facts', detail: 'claim cites zero facts' };
  }

  const inProgress = new Set<string>();
  const settled = new Map<string, TraceabilityResult>();

  const walk = (id: string): TraceabilityResult => {
    const cached = settled.get(id);
    if (cached) return cached;
    if (inProgress.has(id)) {
      return { renderable: false, reason: 'input_cycle', detail: `fact ${JSON.stringify(id)} depends on itself` };
    }
    const fact = factsById.get(id);
    if (!fact) {
      return { renderable: false, reason: 'unknown_input_fact', detail: `no ledger fact ${JSON.stringify(id)}` };
    }

    inProgress.add(id);
    let result: TraceabilityResult;
    switch (fact.provenance) {
      case 'extracted_span':
        result = validSpanFactIds.has(id)
          ? { renderable: true }
          : { renderable: false, reason: 'span_fact_failed_c1', detail: `fact ${JSON.stringify(id)} has no C1-valid span` };
        break;
      case 'user_supplied':
        // §2.1: "has no obligation to be a substring of the document". It grounds a claim on its own.
        result = { renderable: true };
        break;
      case 'closed_world':
        // A closed-world fact is an ABSENCE conclusion. It grounds omission claims (C2b), never a positive one.
        result = {
          renderable: false,
          reason: 'omission_claim_without_scope',
          detail: `fact ${JSON.stringify(id)} is closed_world and cannot support a POSITIVE claim (C2a); route it through C2b`,
        };
        break;
      default: {
        // `derived` — recurse. Every input must itself be traceable; one broken input blocks the whole chain.
        // FAIL-CLOSED on a malformed node (round-4 gate closure): a derived fact whose `input_fact_ids` is absent
        // or not an array is not walkable — that is a NON-RENDERABLE conclusion, never a TypeError. The persisted
        // boundary must hold for records the type system cannot vouch for.
        if (!Array.isArray((fact as { input_fact_ids?: unknown }).input_fact_ids)) {
          result = {
            renderable: false,
            reason: 'unknown_input_fact',
            detail: `fact ${JSON.stringify(id)} is derived but carries no input_fact_ids array — malformed persisted record`,
          };
          break;
        }
        result = { renderable: true };
        for (const input of fact.input_fact_ids) {
          const sub = walk(input);
          if (!sub.renderable) { result = sub; break; }
        }
        break;
      }
    }
    inProgress.delete(id);
    settled.set(id, result);
    return result;
  };

  for (const id of claimFactIds) {
    const r = walk(id);
    if (!r.renderable) return r;
  }
  return { renderable: true };
}

/**
 * C2b. "Every rendered omission claim MUST carry a `search_scope` record with a non-empty enumerated scope. An
 * omission claim with no scope MUST NOT render."
 *
 * The v1.6 `closed_world` provenance is the SOLE carrier. C2b's requirement is unconditional — "every rendered
 * omission claim MUST carry a `search_scope` record with a non-empty enumerated scope" — and a `user_supplied`
 * fact has no scope, by construction.
 *
 * THE USER-SUPPLIED RULING (v1.6 consistency fix). A user who tells us "I did not do X" has made THEIR OWN
 * statement about their own CV. They have not performed a search over an enumerated closed world, and we must not
 * borrow their sentence to make one: rendering it as an omission claim would put the system's voice and the
 * system's implied diligence ("assessed across all 14 entries in your CV") behind an assertion nobody assessed.
 * So a `user_supplied` absence is NOT renderable as an omission claim. It remains fully renderable as what it
 * actually is — the user's own statement — via C2a, which accepts `user_supplied` facts for positive claims and
 * is the only path such a value may reach the surface through. There is deliberately NO omission-claim rendering
 * path for a `user_supplied` fact.
 */
export function checkC2bOmissionClaim(fact: LedgerFact | undefined | null): TraceabilityResult {
  if (!fact) {
    return { renderable: false, reason: 'omission_claim_without_scope', detail: 'omission claim cites no fact' };
  }
  if (fact.provenance === 'user_supplied') {
    return {
      renderable: false,
      reason: 'user_statement_not_omission_claim',
      detail:
        `fact ${JSON.stringify(fact.fact_id)} is the user's own absence statement, which carries no search_scope; ` +
        'render it as a user statement (C2a), never as a system omission claim (C2b)',
    };
  }
  if (fact.provenance !== 'closed_world') {
    return {
      renderable: false,
      reason: 'omission_claim_without_scope',
      detail: `omission claim backed by ${fact.provenance} provenance, which carries no search_scope`,
    };
  }
  const scope = checkSearchScope(fact.search_scope);
  return scope.ok
    ? { renderable: true }
    : { renderable: false, reason: 'scope_invalid', detail: scope.reason ?? 'invalid search_scope' };
}

/* ============================================================================================================== *
 * DEDUPLICATION — §2.1 rule 4, §2.4, §4.2a
 * ============================================================================================================== */

/** One counting unit. Entries sharing a `work_id` collapse to exactly one of these. */
export interface CountingUnit {
  work_id: string;
  /** The union of `entry_ids[]` across the records carrying this `work_id`. Never empty. */
  entry_ids: string[];
  works: WorkRecord[];
  /** Resolved per §2.4 status precedence. */
  counting_status: WorkStatus;
  /** True iff `counting_status === 'withdrawn_retired'` — excluded from every count and breadth test. */
  excluded_from_counts: boolean;
}

export type DedupFailure = 'missing_work_id' | 'empty_entry_ids';

export interface DedupResult {
  units: CountingUnit[];
  errors: Array<{ work_id: string; reason: DedupFailure }>;
}

/**
 * Collapse work records to counting units keyed by `work_id` (which IS the dedup key — the v1 `dedup_group_id`
 * is folded into it, §2.4). `entry_ids[]` is the REQUIRED linkage and may contain one id; a record with an empty
 * `entry_ids[]` is a schema violation and is reported, not silently counted.
 *
 * Returns ALL units, including withdrawn ones, each flagged. Withdrawn works are displayed as stated facts,
 * neutrally — exclusion applies to counting, not to display.
 */
export function groupWorksByWorkId(works: readonly WorkRecord[]): DedupResult {
  const byId = new Map<string, WorkRecord[]>();
  const errors: DedupResult['errors'] = [];

  for (const w of works) {
    if (typeof w.work_id !== 'string' || w.work_id.length === 0) {
      errors.push({ work_id: String(w.work_id), reason: 'missing_work_id' });
      continue;
    }
    if (!Array.isArray(w.entry_ids) || w.entry_ids.length === 0) {
      errors.push({ work_id: w.work_id, reason: 'empty_entry_ids' });
      continue;
    }
    const bucket = byId.get(w.work_id);
    if (bucket) bucket.push(w);
    else byId.set(w.work_id, [w]);
  }

  const units: CountingUnit[] = [];
  for (const [work_id, group] of byId) {
    const entry_ids: string[] = [];
    const seen = new Set<string>();
    const history: StatusHistoryEntry[] = [];
    for (const w of group) {
      for (const id of w.entry_ids) {
        if (!seen.has(id)) {
          seen.add(id);
          entry_ids.push(id);
        }
      }
      history.push(...w.status_history);
    }
    const counting_status = resolveCountingStatus(history);
    units.push({
      work_id,
      entry_ids,
      works: group,
      counting_status,
      excluded_from_counts: counting_status === 'withdrawn_retired',
    });
  }

  return { units, errors };
}

/** The counting units that enter counts and §4.2 breadth tests — i.e. everything except withdrawn works. */
export function countableUnits(units: readonly CountingUnit[]): CountingUnit[] {
  return units.filter((u) => !u.excluded_from_counts);
}

/* ============================================================================================================== *
 * STATUS PRECEDENCE — §2.4, "for counting only"
 * ============================================================================================================== */

/**
 * Fixed order. This is a COUNTING CONVENTION, not a quality ordering, and MUST NOT be displayed as a ladder.
 * Dissemination events are NEVER part of status precedence.
 */
export const STATUS_PRECEDENCE: readonly WorkStatus[] = [
  'published',
  'accepted_in_press',
  'accepted_not_yet_presented',
  'submitted_under_review',
  'in_preparation',
  'withdrawn_retired',
  'status_not_stated',
] as const;

function precedenceRank(s: WorkStatus): number {
  const i = STATUS_PRECEDENCE.indexOf(s);
  return i === -1 ? STATUS_PRECEDENCE.length : i;
}

function isDated(s: StatusHistoryEntry): boolean {
  return typeof s.year === 'number' && Number.isFinite(s.year);
}

/**
 * Resolve a `status_history[]` to EXACTLY ONE counting status, by the §2.4 ordered rule:
 *   1. Dated statuses outrank undated. If ANY status carries a year, only dated statuses are considered.
 *   2. Among dated statuses, the LATEST date wins.
 *   3. Among statuses tied on date, the precedence list wins.
 *   4. Where NO status carries a date, the precedence list wins over the whole undated set.
 *
 * An EMPTY history resolves to `status_not_stated` — absence is a status, never an inference (§2.1 rule 2).
 */
export function resolveCountingStatus(statusHistory: readonly StatusHistoryEntry[]): WorkStatus {
  if (statusHistory.length === 0) return 'status_not_stated';

  const dated = statusHistory.filter(isDated);
  // Rule 1 — dated outranks undated; rule 4 — with no dated status, the whole undated set is considered.
  const considered = dated.length > 0 ? dated : statusHistory.slice();

  let best = considered[0];
  for (const cand of considered.slice(1)) {
    if (dated.length > 0) {
      const bestYear = best.year as number;
      const candYear = cand.year as number;
      // Rule 2 — latest date wins.
      if (candYear > bestYear) {
        best = cand;
        continue;
      }
      if (candYear < bestYear) continue;
      // Rule 3 — tied on date: precedence list.
    }
    // Rules 3 and 4 both resolve by the precedence list.
    if (precedenceRank(cand.status) < precedenceRank(best.status)) best = cand;
  }
  return best.status;
}

/** A work whose RESOLVED counting status is `withdrawn_retired` is excluded from every count and breadth test. */
export function isExcludedFromCounts(statusHistory: readonly StatusHistoryEntry[]): boolean {
  return resolveCountingStatus(statusHistory) === 'withdrawn_retired';
}

/* ============================================================================================================== *
 * `activity_status` DECISION RULES — §2.2
 * ============================================================================================================== */

/** The rule that fired. Internal — never displayed. */
export type ActivityStatusRule = '0' | '1a' | '1b' | '1c' | '2a' | '2b' | '2c' | '3';

/** Reference to a ledger fact that a `derived` activity_status maps from. */
export interface FactRef {
  fact_id: string;
}

/** The enumerable signals rules 0–3 read. Every one of these is itself a ledger fact or a span on the entry. */
export interface ActivityStatusSignals {
  /** Rule 1a — an explicit stated completion clause ("completed", "defended", "term completed"). */
  completion_clause: { fact_id: string; span: EvidenceSpan } | null;
  /** Rule 2a — an explicit stated continuation clause ("ongoing", "current", "present"). */
  continuation_clause: { fact_id: string; span: EvidenceSpan } | null;
  /** Rule 1b — the `date_end` fact. */
  date_end: { fact_id: string; value: DatePartial } | null;
  /** Rule 2b — the `is_ongoing` fact. */
  is_ongoing: { fact_id: string; value: boolean | NotStated } | null;
  /** Rule 1c — the stated date/year the past-tense description carries. */
  stated_date: { fact_id: string; value: DatePartial } | null;
  /** Rule 1c — evidence that the activity is described in the past tense. */
  past_tense_evidence: { fact_id: string; span: EvidenceSpan } | null;
  /** Rule 2c — evidence of a continuing present tense ("currently screening abstracts"). */
  continuing_present_tense_evidence: { fact_id: string; span: EvidenceSpan } | null;
}

export interface ActivityStatusDecision {
  activity_status: ActivityStatus;
  /** Which rule fired. Internal, never displayed. */
  rule: ActivityStatusRule;
  /** The provenance the resulting fact carries, per the §2.2 provenance mapping. */
  provenance: Provenance;
  /** Rule 0 only: entry confidence drops to Moderate under the §9.1 ambiguity rule. */
  confidence_cap: 'Moderate' | null;
  /**
   * Signal names REFUSED by the §2.2 publication-status ruling before any rule ran (see
   * `isCitationStatusExpression`). Telemetry: it is how the caller can see that a citation status was offered as an
   * activity clause and declined. Empty in the ordinary case.
   */
  excluded_signals: string[];
}

/* -------------------------------------------------------------------------------------------------------------- *
 * §2.2 PUBLICATION-STATUS RULING (added codebook v1.6) — ENGINE-ENFORCED, not prompt-enforced.
 *
 * Verbatim: 'The word "Published" — and every other publication-status word appearing inside a citation
 * (`in press`, `accepted`, `submitted`, `epub ahead of print`) — is a `status_history` fact, **NOT** an activity
 * clause, and **MUST NOT** fire Rule 1a. A citation is a record of a work's status, not a stated description of
 * what the applicant did.'
 *
 * WHY IT LIVES HERE AND NOT ONLY IN THE PROMPT. The prompt already tells the model that "Published" in a citation
 * is a status; but the prompt is a request, and the ledger's guarantees may not rest on the model having honoured
 * one. A model that returns `signals.completion_clause = "Published"` supplies a quote that is genuinely verbatim
 * in the entry, so it passes C1 and C3 cleanly and fires Rule 1a — the contract is broken by a perfectly valid
 * span. The only place the ruling can actually bind is the rule engine's own input validation, which is here.
 * -------------------------------------------------------------------------------------------------------------- */

/**
 * Publication/manuscript status vocabulary. These words describe where a WORK sits in the publication process; they
 * never describe an activity the applicant performed.
 */
const CITATION_STATUS_TERMS: readonly RegExp[] = [
  /\bpublished\b/i,
  /\bin\s+press\b/i,
  /\bepub(\s+ahead\s+of\s+print)?\b/i,
  /\bahead\s+of\s+print\b/i,
  /\baccepted(\s+for\s+publication)?\b/i,
  /\bsubmitted\b/i,
  /\bunder\s+review\b/i,
  /\bin\s+review\b/i,
  /\bresubmitted\b/i,
  /\brevised\s+and\s+resubmitted\b/i,
  /\bforthcoming\b/i,
  /\bin\s+preparation\b/i,
  /\bpreprint\b/i,
  /\bretracted\b/i,
  /\bwithdrawn\b/i,
  // FRENCH equivalents (live-audit calibration + INV-4: French CVs must band identically). Same claim, same
  // refusal. NOTE: `\b` is ASCII-only in JS and fails beside accented letters ("Publié," has no \b after é), so
  // the French patterns use explicit Unicode letter boundaries instead.
  /(^|\P{L})publi[ée]e?s?(\P{L}|$)/iu,
  /(^|\P{L})sous\s+presse(\P{L}|$)/iu,
  /(^|\P{L})soumise?s?(\P{L}|$)/iu,
  /(^|\P{L})accept[ée]e?s?(\P{L}|$)/iu,
  /(^|\P{L})en\s+pr[ée]paration(\P{L}|$)/iu,
  /(^|\P{L})pr[ée]publication(\P{L}|$)/iu,
];

/**
 * DISSEMINATION-EVENT vocabulary (live-audit calibration). "Presented at CCC 2023", "Poster, CAS Annual Meeting"
 * describe a dissemination EVENT of a work — §2.4 `dissemination_events` — not a stated activity completion. The
 * dominant live-audit failure was Rule 1a firing `completed` off exactly these clauses on bare citation entries.
 * Same conservative shape as the status terms: refusal only when NO real completion word accompanies the event
 * term, and every refusal moves toward `not_stated` — the safe direction.
 */
const DISSEMINATION_EVENT_TERMS: readonly RegExp[] = [
  // NOUN-ONLY event vocabulary (gate-run refinement). The seed conventions are labelled BY RULE: a VERBED
  // presentation clause ("Poster presented at ... April 2023") is legitimate past-tense evidence and rule 1c
  // correctly gives completed; only a verbless NOUN PHRASE ("Oral presentation, ... June 2023") states no
  // activity. So event nouns refuse a signal only when no presentation VERB accompanies them — see
  // EVENT_VERB_FORMS below. "presented patients" (clinical work) never matched anything here.
  /\bpresentations?\b/i,
  /\bposter\b/i,
  /\boral\s+presentation\b/i,
  /\bpodium\b/i,
  /\bkeynote\b/i,
  /\binvited\s+talk\b/i,
  /\babstract\b/i,
  // French noun forms — Unicode boundaries (ASCII \b fails beside accented letters).
  /(^|\P{L})communication\s+(orale|par\s+affiche)(\P{L}|$)/iu,
  /(^|\P{L})affiches?(\P{L}|$)/iu,
];

/**
 * STATE-ENTRY vocabulary (live-audit calibration, round 3). "Elected member, 2024" and "Participation
 * certificate" describe entry into an ongoing STATE or bare participation — §3.1 Participatory vocabulary — not a
 * completed activity. Rule 1c was firing `completed` off "elected" + a past year. Conferral verbs that the seed
 * conventions DO read as completion ("Awarded…", "Nominated by…") are deliberately NOT here.
 */
/** A presentation VERB in the quote licenses rule 1c: the clause DESCRIBES a past activity, not just names one. */
const EVENT_VERB_FORMS: RegExp = /\bpresented\b|(^|\P{L})pr[ée]sent[ée]e?s?\s+(à|au|aux)(\P{L}|$)/iu;

const STATE_ENTRY_TERMS: readonly RegExp[] = [
  /\belected\b/i,
  /\binducted\b/i,
  /\bparticipation\b/i,
  /(^|\P{L})élue?s?(\P{L}|$)/iu,
  /(^|\P{L})intronis[ée]e?s?(\P{L}|$)/iu,
];

/**
 * The §2.2 Rule 1a completion vocabulary, verbatim from the codebook: '"completed," "delivered," "finished,"
 * "defended," "concluded," "term completed," or an equivalent stated completion clause'.
 */
const COMPLETION_TERMS: readonly RegExp[] = [
  /\bcomplet(e|ed|ing|ion)\b/i,
  /\bdeliver(ed|y)\b/i,
  /\bfinish(ed)?\b/i,
  /\bdefend(ed)?\b/i,
  /\bconclud(ed|ing)\b/i,
  /\bwrapped\s+up\b/i,
  // French equivalents (INV-4) — Unicode boundaries, not ASCII \b (see the citation-terms note).
  /(^|\P{L})compl[ée]t[ée]e?s?(\P{L}|$)/iu,
  /(^|\P{L})termin[ée]e?s?(\P{L}|$)/iu,
  /(^|\P{L})achev[ée]e?s?(\P{L}|$)/iu,
  /(^|\P{L})livr[ée]e?s?(\P{L}|$)/iu,
  /(^|\P{L})soutenue?s?(\P{L}|$)/iu,
];

/**
 * Is this quoted clause a CITATION STATUS rather than a stated activity clause?
 *
 * The test is deliberately narrow, and deliberately conservative in one direction only. A clause is refused iff
 * BOTH hold:
 *   (a) it contains at least one publication-status term, AND
 *   (b) it contains NO §2.2 completion-vocabulary word — i.e. the ONLY thing making it look like completion is the
 *       publication-status word itself.
 *
 * So `"Published, Can J Anaesth 2025"` is refused, while `"Completed the protocol"` and `"Manuscript submitted
 * after I completed data collection"` both survive: the second one states a real completion clause alongside the
 * status, and the codebook excludes the status word, not the sentence containing it.
 *
 * Every refusal moves the entry AWAY from `completed` and toward `not_stated` or a later rule, which is the safe
 * direction: the cost of a false refusal is an activity status we decline to assert, and §2.1 rule 2 already
 * insists that is not a deficiency. The cost of a false ACCEPT is a fabricated claim that the applicant finished
 * something, sourced from a word about a journal.
 */
export function isCitationStatusExpression(quotedText: string): boolean {
  if (typeof quotedText !== 'string' || quotedText.length === 0) return false;
  const eventNounOnly =
    DISSEMINATION_EVENT_TERMS.some((re) => re.test(quotedText)) && !EVENT_VERB_FORMS.test(quotedText);
  const hasStatus =
    CITATION_STATUS_TERMS.some((re) => re.test(quotedText)) ||
    eventNounOnly ||
    STATE_ENTRY_TERMS.some((re) => re.test(quotedText));
  if (!hasStatus) return false;
  return !COMPLETION_TERMS.some((re) => re.test(quotedText));
}

/**
 * The CONTINUATION-side filter (Sol final review: the completion-oriented predicate is too broad here). A real
 * continuation clause may legitimately mention an event noun — "currently preparing a poster" is ongoing activity,
 * and refusing it would break Rule 2. What masquerades as continuation is only the manuscript-STATUS vocabulary
 * ("Under review", "In preparation", "sous presse"), so only CITATION_STATUS_TERMS are refused, with the same
 * completion-word escape. Event and state-entry terms are NOT tested on this side.
 */
export function isCitationStatusContinuation(quotedText: string): boolean {
  if (typeof quotedText !== 'string' || quotedText.length === 0) return false;
  if (!CITATION_STATUS_TERMS.some((re) => re.test(quotedText))) return false;
  // Sol round-8: a MIXED clause that independently states continuing activity ("under review while I am
  // currently collecting follow-up data") is real continuation evidence — refuse only status-only quotes.
  if (/\b(currently|still|ongoing|continuing)\b|en\s+cours|actuellement|toujours/iu.test(quotedText)) return false;
  return !COMPLETION_TERMS.some((re) => re.test(quotedText));
}

/**
 * Is a date-partial WHOLLY in the past relative to the review date?
 *   `YYYY`    → the whole year must precede the review year.
 *   `YYYY-MM` → the whole month must precede the review month.
 * `not_stated` and malformed values are never "wholly in the past" — absence is never an inference (§2.1 rule 2).
 */
export function isWhollyPast(date: DatePartial, reviewDate: string): boolean {
  if (date === NOT_STATED) return false;
  const m = /^(\d{4})(?:-(\d{2}))?$/.exec(date);
  if (!m) return false;
  const r = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(reviewDate);
  if (!r) return false;
  const y = Number(m[1]);
  const ry = Number(r[1]);
  if (m[2] === undefined) return y < ry;
  const mo = Number(m[2]);
  const rm = Number(r[2]);
  return y < ry || (y === ry && mo < rm);
}

/**
 * Decide `activity_status` per §2.2.
 *
 * Rule 0 (conflict-first) is evaluated FIRST: an explicit completion statement AND an explicit continuation
 * statement together ⇒ `not_stated`, with entry confidence dropped to Moderate. Over the remaining rules,
 * FIRST MATCH WINS in the order 1a, 1b, 1c, 2a, 2b, 2c, 3.
 *
 * Provenance mapping (§2.2, verbatim):
 *   1a / 2a → `extracted_span` over the completion/continuation clause.
 *   1b      → `derived` from the `date_end` fact.
 *   1c      → `derived` from the date fact AND the tense evidence span.
 *   2b      → `derived` from the `is_ongoing` fact, NOT from any date fact.
 *   2c      → `derived` from the tense evidence span.
 *   0 / 3   → `derived` from whatever inputs the rule read (rule 0: both conflicting clauses).
 *
 * @param reviewDate CONFIGURATION (`YYYY-MM` or `YYYY-MM-DD`), not a ledger fact, and never displayed (§2.2).
 */
/**
 * The closed world Rule 3 assessed. Supplied by the caller because only the caller knows WHICH entry was searched
 * and which extractor version searched it — the rule cannot invent its own scope (that would be exactly the
 * fabricated closed-world claim §2.1 rule 2 forbids).
 */
export interface ActivityStatusScope {
  entry_id: string;
  extractor_version: string;
}

export function decideActivityStatus(
  signals: ActivityStatusSignals,
  reviewDate: string,
  scope: ActivityStatusScope,
): ActivityStatusDecision {
  const {
    continuation_clause: raw_continuation_clause,
    date_end,
    is_ongoing,
    stated_date,
    continuing_present_tense_evidence: raw_continuing_present_tense_evidence,
  } = signals;

  // ---- INPUT VALIDATION — the §2.2 publication-status ruling (v1.6), applied BEFORE any rule reads a signal. ---
  //
  // `completion_clause` (Rule 1a) and `past_tense_evidence` (Rule 1c) are the two inputs that can turn a word into
  // `activity_status = completed`, and they are exactly the two a citation status can masquerade as. A signal
  // whose quote is a citation status is REMOVED from the signal set here, so no rule ever sees it — including
  // Rule 0, whose conflict test would otherwise be decided by a word the codebook says is not an activity clause
  // at all. The status itself is not lost: it belongs to the work record's `status_history` (§2.4), which is where
  // extraction already routes it, and the entry falls through to Rule 3's honest `not_stated`.
  const excluded_signals: string[] = [];
  const admit = (
    name: string,
    signal: { fact_id: string; span: EvidenceSpan } | null,
  ): { fact_id: string; span: EvidenceSpan } | null => {
    if (signal === null) return null;
    if (isCitationStatusExpression(signal.span.quoted_text)) {
      excluded_signals.push(name);
      return null;
    }
    return signal;
  };
  const completion_clause = admit('completion_clause', signals.completion_clause);
  const past_tense_evidence = admit('past_tense_evidence', signals.past_tense_evidence);
  // CONTINUATION signals are filtered through the same ruling (live-audit calibration round 7): "Under review"
  // and "Manuscript in preparation" are manuscript statuses that masquerade as continuation exactly as
  // "Published" masquerades as completion — Rule 2a was minting `under_way` off a bare citation's status line.
  // The status still reaches `status_history`; the entry falls through to Rule 3's honest `not_stated`.
  const admitContinuation = (
    name: string,
    signal: { fact_id: string; span: EvidenceSpan } | null,
  ): { fact_id: string; span: EvidenceSpan } | null => {
    if (signal === null) return null;
    if (isCitationStatusContinuation(signal.span.quoted_text)) {
      excluded_signals.push(name);
      return null;
    }
    return signal;
  };
  const continuation_clause = admitContinuation('continuation_clause', raw_continuation_clause);
  const continuing_present_tense_evidence = admitContinuation(
    'continuing_present_tense_evidence',
    raw_continuing_present_tense_evidence,
  );

  // ---- Rule 0 — conflict handling, evaluated FIRST. ----------------------------------------------------------
  if (completion_clause !== null && continuation_clause !== null) {
    return {
      activity_status: NOT_STATED,
      rule: '0',
      provenance: {
        provenance: 'derived',
        rule_id: 'activity_status.rule_0_conflict',
        input_fact_ids: [completion_clause.fact_id, continuation_clause.fact_id],
      },
      confidence_cap: 'Moderate',
      excluded_signals,
    };
  }

  // ---- Rule 1 — completed. First match wins from here. -------------------------------------------------------
  // 1a — stated completion clause.
  if (completion_clause !== null) {
    return {
      activity_status: 'completed',
      rule: '1a',
      provenance: { provenance: 'extracted_span', evidence_span: completion_clause.span },
      confidence_cap: null,
      excluded_signals,
    };
  }
  // 1b — a stated end date wholly in the past, with is_ongoing NOT true.
  if (
    date_end !== null &&
    isWhollyPast(date_end.value, reviewDate) &&
    !(is_ongoing !== null && is_ongoing.value === true)
  ) {
    return {
      activity_status: 'completed',
      rule: '1b',
      provenance: {
        provenance: 'derived',
        rule_id: 'activity_status.rule_1b_past_end_date',
        input_fact_ids: [date_end.fact_id],
      },
      confidence_cap: null,
      excluded_signals,
    };
  }
  // 1c — past-tense description AND a stated date/year wholly in the past.
  if (past_tense_evidence !== null && stated_date !== null && isWhollyPast(stated_date.value, reviewDate)) {
    return {
      activity_status: 'completed',
      rule: '1c',
      provenance: {
        provenance: 'derived',
        rule_id: 'activity_status.rule_1c_past_tense_past_date',
        input_fact_ids: [stated_date.fact_id, past_tense_evidence.fact_id],
      },
      confidence_cap: null,
      excluded_signals,
    };
  }

  // ---- Rule 2 — under_way. -----------------------------------------------------------------------------------
  // 2a — stated continuation clause.
  if (continuation_clause !== null) {
    return {
      activity_status: 'under_way',
      rule: '2a',
      provenance: { provenance: 'extracted_span', evidence_span: continuation_clause.span },
      confidence_cap: null,
      excluded_signals,
    };
  }
  // 2b — is_ongoing is true. Derived from the is_ongoing fact, NOT from any date fact.
  if (is_ongoing !== null && is_ongoing.value === true) {
    return {
      activity_status: 'under_way',
      rule: '2b',
      provenance: {
        provenance: 'derived',
        rule_id: 'activity_status.rule_2b_is_ongoing',
        input_fact_ids: [is_ongoing.fact_id],
      },
      confidence_cap: null,
      excluded_signals,
    };
  }
  // 2c — continuing present tense.
  if (continuing_present_tense_evidence !== null) {
    return {
      activity_status: 'under_way',
      rule: '2c',
      provenance: {
        provenance: 'derived',
        rule_id: 'activity_status.rule_2c_continuing_present_tense',
        input_fact_ids: [continuing_present_tense_evidence.fact_id],
      },
      confidence_cap: null,
      excluded_signals,
    };
  }

  // ---- Rule 3 — not_stated otherwise. MUST NOT render as zero, absent, or lacking (§2.1 rule 2). --------------
  //
  // CODEBOOK v1.6 RESOLUTION of the tension v1.5 left open (it was reported, not reinterpreted, and the owner
  // ratified the fix). v1.5 said every non-1a/2a status is `derived`, while §2.1 requires a `derived` fact to
  // carry the ids of its inputs — and Rule 3 fires precisely when the entry states NOTHING to derive from, so on
  // a bare-title entry there are zero inputs and the fact could not validate.
  //
  // v1.6 mints `closed_world` here instead. That is the honest description of what Rule 3 did: it searched an
  // enumerated scope (this entry, the completion/continuation/tense/date fields) and found no stated or tensed
  // activity clause. An absence conclusion is not read and not computed — it is ASSESSED over a documented closed
  // world, which is §2.7 Form 2. The fact now validates, and it validates for the right reason.
  return {
    activity_status: NOT_STATED,
    rule: '3',
    provenance: {
      provenance: 'closed_world',
      search_scope: {
        claim_type: 'activity_status_not_stated',
        scope_description:
          `entry ${scope.entry_id}: searched for a stated completion clause, a stated continuation clause, ` +
          'past-tense evidence with a stated past date, continuing-present-tense evidence, and a stated end date; ' +
          'none was found' +
          // HONESTY OF THE SCOPE RECORD. When the publication-status ruling refused a signal, something WAS
          // offered and declined — "none was found" alone would overstate the search. The scope says so, because
          // "show source" on this omission claim opens exactly this description (§2.7 Form 2).
          (excluded_signals.length > 0
            ? `; ${excluded_signals.join(' and ')} quoted a publication status, which §2.2 rules is a status_history fact and not an activity clause`
            : ''),
        entry_ids_assessed: [scope.entry_id],
        n_assessed: 1,
        extractor_version: scope.extractor_version,
      },
    },
    confidence_cap: null,
    excluded_signals,
  };
}
